import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../axios";
import Input from "../components/Input";
import MatchSpecificationOutput, {
    TableRow,
    AuditResult,
} from "../components/MatchSpecificationOutput";

// ─── Signature Canvas ───────────────────────────────────────────────────────

function SignatureCanvas({
    canvasRef,
    onSignatureChange,
}: {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    onSignatureChange: (hasSignature: boolean) => void;
}) {
    const drawing = useRef(false);
    const [signed, setSigned] = useState(false);

    const getPos = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        if ("touches" in e) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY,
            };
        }
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    };

    const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        drawing.current = true;
        const ctx = canvasRef.current!.getContext("2d")!;
        const { x, y } = getPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        if (!drawing.current) return;
        const ctx = canvasRef.current!.getContext("2d")!;
        ctx.strokeStyle = "#1e293b";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        const { x, y } = getPos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDraw = () => {
        if (!drawing.current) return;
        drawing.current = false;
        setSigned(true);
        onSignatureChange(true);
    };

    const clear = () => {
        const canvas = canvasRef.current!;
        canvas.getContext("2d")!.clearRect(0, 0, canvas.width, canvas.height);
        setSigned(false);
        onSignatureChange(false);
    };

    const getBase64 = () => canvasRef.current?.toDataURL("image/png") ?? "";

    // Expose getBase64 via ref-like pattern using a data attribute trick
    useEffect(() => {
        if (canvasRef.current) {
            (canvasRef.current as any).__getBase64 = getBase64;
        }
    });

    return (
        <div>
            <canvas
                ref={canvasRef}
                width={400}
                height={120}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={stopDraw}
                style={{
                    width: "100%",
                    height: 120,
                    border: `1.5px solid ${signed ? "#6366f1" : "#d1d5db"}`,
                    borderRadius: 8,
                    background: "#fafafa",
                    cursor: "crosshair",
                    display: "block",
                    touchAction: "none",
                }}
            />
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 6,
                }}
            >
                <button
                    onClick={clear}
                    style={{
                        fontSize: 11,
                        color: "#6b7280",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                    }}
                >
                    Clear signature
                </button>
            </div>
        </div>
    );
}

// ─── Audit + Signature Panel ─────────────────────────────────────────────────

function AuditPanel({
    auditedCount,
    totalCount,
    onSave,
    saving,
    existingAuditBy,
    existingAuditSign,
    onAuditAgain,
    onSaveWithoutAudit,
    savingWithoutAudit,
    hasResult,
}: {
    auditedCount: number;
    totalCount: number;
    onSave: (sign: string) => void;
    saving: boolean;
    existingAuditBy: string | null;
    existingAuditSign: string | null;
    onAuditAgain: () => void;
    onSaveWithoutAudit: () => void;
    savingWithoutAudit: boolean;
    hasResult: boolean;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hasSig, setHasSig] = useState(false);
    const [auditingAgain, setAuditingAgain] = useState(false);
    const allAudited = totalCount > 0 && auditedCount >= totalCount;
    const canSave = allAudited && hasSig;
    const progress = totalCount > 0 ? (auditedCount / totalCount) * 100 : 0;

    const handleSave = () => {
        const base64 = canvasRef.current?.toDataURL("image/png") ?? "";
        onSave(base64);
    };

    if (existingAuditSign && !auditingAgain) {
        return (
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    overflow: "hidden",
                    marginBottom: 16,
                }}
            >
                <div
                    style={{
                        padding: "10px 14px",
                        borderBottom: "1px solid #e2e8f0",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase" as const,
                        letterSpacing: 0.8,
                        color: "#16a34a",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                    }}
                >
                    <i className="bi bi-patch-check-fill" />
                    Audited
                </div>
                <div style={{ padding: 14 }}>
                    <div style={{ marginBottom: 12 }}>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: "uppercase" as const,
                                letterSpacing: 0.6,
                                color: "#64748b",
                                marginBottom: 4,
                            }}
                        >
                            Audited By
                        </div>
                        <div
                            style={{
                                fontSize: 13,
                                color: "#1e293b",
                                fontWeight: 500,
                            }}
                        >
                            {existingAuditBy || "—"}
                        </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: "uppercase" as const,
                                letterSpacing: 0.6,
                                color: "#64748b",
                                marginBottom: 6,
                            }}
                        >
                            Signature
                        </div>
                        <img
                            src={existingAuditSign}
                            alt="Audit signature"
                            style={{
                                width: "100%",
                                border: "1px solid #e2e8f0",
                                borderRadius: 8,
                                background: "#fafafa",
                                display: "block",
                            }}
                        />
                    </div>
                    <button
                        onClick={() => {
                            setAuditingAgain(true);
                            onAuditAgain();
                        }}
                        style={{
                            width: "100%",
                            padding: "9px 0",
                            borderRadius: 8,
                            border: "1px solid #cbd5e1",
                            background: "#f8fafc",
                            color: "#475569",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            marginBottom: 8,
                        }}
                    >
                        <i className="bi bi-arrow-clockwise me-1" />
                        Audit Again
                    </button>
                    <button
                        disabled={!hasResult || savingWithoutAudit}
                        onClick={onSaveWithoutAudit}
                        style={{
                            width: "100%",
                            padding: "9px 0",
                            borderRadius: 8,
                            border: "1px dashed #94a3b8",
                            background: "transparent",
                            color: "#64748b",
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: hasResult ? "pointer" : "not-allowed",
                            opacity: hasResult ? 1 : 0.5,
                        }}
                    >
                        <i className="bi bi-floppy me-1" />
                        {savingWithoutAudit ? "Saving…" : "Save without auditing"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                overflow: "hidden",
                marginBottom: 16,
            }}
        >
            {/* Audit progress */}
            <div
                style={{
                    padding: "10px 14px",
                    borderBottom: "1px solid #e2e8f0",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    letterSpacing: 0.8,
                    color: "#6366f1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <span>
                    <i className="bi bi-patch-check me-1" />
                    Audit Progress
                </span>
                <span
                    style={{
                        color: allAudited ? "#16a34a" : "#64748b",
                        fontWeight: 700,
                    }}
                >
                    {auditedCount} / {totalCount}
                </span>
            </div>

            <div style={{ padding: "14px 14px 0" }}>
                {/* Progress bar */}
                <div
                    style={{
                        background: "#e2e8f0",
                        borderRadius: 99,
                        height: 8,
                        marginBottom: 12,
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            width: `${progress}%`,
                            height: "100%",
                            borderRadius: 99,
                            background: allAudited ? "#16a34a" : "#6366f1",
                            transition: "width 0.3s ease",
                        }}
                    />
                </div>

                {!allAudited && (
                    <p
                        style={{
                            fontSize: 12,
                            color: "#9ca3af",
                            marginBottom: 12,
                        }}
                    >
                        Tick every row to unlock the save button.
                    </p>
                )}
                {allAudited && (
                    <p
                        style={{
                            fontSize: 12,
                            color: "#16a34a",
                            fontWeight: 600,
                            marginBottom: 12,
                        }}
                    >
                        ✓ All rows audited
                    </p>
                )}
            </div>

            {/* Signature area */}
            <div style={{ padding: "0 14px 14px" }}>
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase" as const,
                        letterSpacing: 0.6,
                        color: "#64748b",
                        marginBottom: 6,
                    }}
                >
                    Signature
                </div>
                <SignatureCanvas
                    canvasRef={canvasRef}
                    onSignatureChange={setHasSig}
                />
                {!hasSig && (
                    <p
                        style={{
                            fontSize: 12,
                            color: "#9ca3af",
                            marginTop: 6,
                            marginBottom: 0,
                        }}
                    >
                        Sign in the box above
                    </p>
                )}
            </div>

            {/* Save button */}
            <div style={{ padding: "0 14px 14px" }}>
                <button
                    disabled={!canSave || saving}
                    onClick={handleSave}
                    style={{
                        width: "100%",
                        padding: "10px 0",
                        borderRadius: 8,
                        border: "none",
                        background: canSave ? "#6366f1" : "#e2e8f0",
                        color: canSave ? "#fff" : "#9ca3af",
                        fontSize: 14,
                        fontWeight: 700,
                        cursor: canSave ? "pointer" : "not-allowed",
                        transition: "background 0.2s",
                        marginBottom: 8,
                    }}
                >
                    {saving
                        ? "Saving…"
                        : !allAudited
                          ? `Audit all rows first (${totalCount - auditedCount} remaining)`
                          : !hasSig
                            ? "Sign to save"
                            : "Save Specification"}
                </button>
                <button
                    disabled={!hasResult || savingWithoutAudit}
                    onClick={onSaveWithoutAudit}
                    style={{
                        width: "100%",
                        padding: "9px 0",
                        borderRadius: 8,
                        border: "1px dashed #94a3b8",
                        background: "transparent",
                        color: "#64748b",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: hasResult ? "pointer" : "not-allowed",
                        opacity: hasResult ? 1 : 0.5,
                    }}
                >
                    <i className="bi bi-floppy me-1" />
                    {savingWithoutAudit ? "Saving…" : "Save without auditing"}
                </button>
            </div>
        </div>
    );
}

// ─── Row Detail Panel ────────────────────────────────────────────────────────

function ratingColor(r: any): string {
    const v = parseFloat(r);
    if (isNaN(v)) return "#9ca3af";
    if (v >= 0.4) return "#16a34a";
    if (v >= 0.2) return "#ca8a04";
    return "#dc2626";
}

function RowDetailSection({
    row,
    expectedResults,
    allRatings,
    onRatingDeleted,
}: {
    row: TableRow | null;
    expectedResults: any[];
    allRatings: any[];
    onRatingDeleted?: (id: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleDeleteRating = async (id: string) => {
        setDeletingId(id);
        try {
            await API.deleteRating(id);
            onRatingDeleted?.(id);
        } catch (err) {
            console.error("Failed to delete rating:", err);
        } finally {
            setDeletingId(null);
        }
    };

    if (!row) {
        return (
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    overflow: "hidden",
                    marginBottom: 16,
                }}
            >
                <button
                    onClick={() => setOpen((v) => !v)}
                    style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase" as const,
                        letterSpacing: 0.8,
                        color: "#6366f1",
                    }}
                >
                    Row Details
                    <i className={`bi bi-chevron-${open ? "up" : "down"}`} style={{ fontSize: 12 }} />
                </button>
                {open && (
                    <div
                        style={{
                            padding: "20px 14px",
                            textAlign: "center",
                            color: "#9ca3af",
                            fontSize: 13,
                            borderTop: "1px solid #e2e8f0",
                        }}
                    >
                        Click any row in the table to see its expected values and ratings
                    </div>
                )}
            </div>
        );
    }

    const erDoc = expectedResults.find((er) => er.key === row.rawMainCategory);
    const specKey = row.rawSpecName;
    const subData = erDoc?.json?.[row.rawSubCategory];
    const expectedValues: string[] | null =
        subData && Array.isArray(subData[specKey]) ? subData[specKey] : null;
    const isStringField = subData && typeof subData[specKey] === "string";

    const matchingRatings = allRatings.filter(
        (r) => r.key?.trim().toLowerCase() === specKey.trim().toLowerCase(),
    );

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                overflow: "hidden",
                marginBottom: 16,
            }}
        >
            <button
                onClick={() => setOpen((v) => !v)}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    background: "none",
                    border: "none",
                    borderBottom: open ? "1px solid #e2e8f0" : "none",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    letterSpacing: 0.8,
                    color: "#6366f1",
                }}
            >
                <span>
                    Row Details
                    {row && (
                        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, textTransform: "none" as const, color: "#64748b", letterSpacing: 0 }}>
                            {row.specName}
                        </span>
                    )}
                </span>
                <i className={`bi bi-chevron-${open ? "up" : "down"}`} style={{ fontSize: 12 }} />
            </button>

            {open && <div style={{ padding: "12px 14px" }}>
                <div style={{ marginBottom: 12 }}>
                    <span
                        style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: "#1e293b",
                        }}
                    >
                        {row.specName}
                    </span>
                    <span
                        style={{
                            marginLeft: 8,
                            fontSize: 11,
                            background: "#f1f5f9",
                            color: "#64748b",
                            padding: "2px 7px",
                            borderRadius: 4,
                        }}
                    >
                        {row.uiGroup}
                    </span>
                </div>
                <div
                    style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}
                >
                    {row.mainCategory} › {row.subCategory}
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase" as const,
                            letterSpacing: 0.6,
                            color: "#64748b",
                            marginBottom: 6,
                        }}
                    >
                        Current Rating
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                        }}
                    >
                        <span style={{ fontSize: 14, color: "#374151" }}>
                            {row.value === false ? "—" : row.value || "—"}
                        </span>
                        {row.rating !== "-" ? (
                            <span
                                style={{
                                    display: "inline-block",
                                    padding: "2px 10px",
                                    borderRadius: 20,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    background: ratingColor(row.rating) + "18",
                                    color: ratingColor(row.rating),
                                    border: `1px solid ${ratingColor(row.rating)}40`,
                                }}
                            >
                                {row.rating}
                            </span>
                        ) : (
                            <span style={{ color: "#d1d5db", fontSize: 13 }}>
                                no rating
                            </span>
                        )}
                    </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase" as const,
                            letterSpacing: 0.6,
                            color: "#64748b",
                            marginBottom: 6,
                        }}
                    >
                        Expected Values
                    </div>
                    {expectedValues ? (
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap" as const,
                                gap: 6,
                            }}
                        >
                            {expectedValues.map((val, i) => (
                                <button
                                    key={i}
                                    onClick={() =>
                                        navigator.clipboard?.writeText(val)
                                    }
                                    title="Click to copy"
                                    style={{
                                        padding: "3px 10px",
                                        borderRadius: 20,
                                        fontSize: 12,
                                        fontWeight: 500,
                                        background: "#f0fdf4",
                                        color: "#15803d",
                                        border: "1px solid #bbf7d0",
                                        cursor: "pointer",
                                    }}
                                >
                                    {val}
                                </button>
                            ))}
                        </div>
                    ) : isStringField ? (
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>
                            Free text field — no enumerated values
                        </span>
                    ) : (
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>
                            Not defined in expected results
                        </span>
                    )}
                </div>

                <div>
                    <div
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase" as const,
                            letterSpacing: 0.6,
                            color: "#64748b",
                            marginBottom: 6,
                        }}
                    >
                        Known Ratings
                    </div>
                    {matchingRatings.length > 0 ? (
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: 12,
                            }}
                        >
                            <thead>
                                <tr style={{ background: "#f8fafc" }}>
                                    <th
                                        style={{
                                            padding: "5px 8px",
                                            textAlign: "left",
                                            fontWeight: 600,
                                            color: "#64748b",
                                            borderBottom: "1px solid #e2e8f0",
                                        }}
                                    >
                                        Value
                                    </th>
                                    <th
                                        style={{
                                            padding: "5px 8px",
                                            textAlign: "center",
                                            fontWeight: 600,
                                            color: "#64748b",
                                            borderBottom: "1px solid #e2e8f0",
                                        }}
                                    >
                                        Rating
                                    </th>
                                    <th style={{ padding: "5px 8px", borderBottom: "1px solid #e2e8f0", width: 32 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {matchingRatings.map((r, i) => (
                                    <tr
                                        key={i}
                                        style={{
                                            borderBottom: "1px solid #f1f5f9",
                                        }}
                                    >
                                        <td
                                            style={{
                                                padding: "5px 8px",
                                                color: "#374151",
                                            }}
                                        >
                                            {r.value || "—"}
                                        </td>
                                        <td
                                            style={{
                                                padding: "5px 8px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    padding: "1px 8px",
                                                    borderRadius: 20,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    background:
                                                        ratingColor(r.rating) +
                                                        "18",
                                                    color: ratingColor(
                                                        r.rating,
                                                    ),
                                                    border: `1px solid ${ratingColor(r.rating)}40`,
                                                }}
                                            >
                                                {r.rating}
                                            </span>
                                        </td>
                                        <td style={{ padding: "3px 6px", textAlign: "center" }}>
                                            <button
                                                onClick={() => handleDeleteRating(r._id)}
                                                disabled={deletingId === r._id}
                                                title="Delete rating"
                                                style={{
                                                    background: "none", border: "none", cursor: deletingId === r._id ? "not-allowed" : "pointer",
                                                    color: deletingId === r._id ? "#d1d5db" : "#ef4444",
                                                    padding: 2, fontSize: 13, lineHeight: 1, display: "inline-flex",
                                                }}
                                            >
                                                <i className={deletingId === r._id ? "bi bi-hourglass-split" : "bi bi-trash3"} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <span style={{ fontSize: 12, color: "#9ca3af" }}>
                            No ratings defined for this spec
                        </span>
                    )}
                </div>
            </div>}
        </div>
    );
}

// ─── AI Audit Result Card ────────────────────────────────────────────────────

const PIPELINE_STEPS = [
    "Fetched current value",
    "Queried web sources",
    "Analysis complete",
];

function AuditResultCard({
    rowInfo,
    result,
    onDismiss,
    vehicleTitle,
}: {
    rowInfo: AuditResult["rowInfo"];
    result: AuditResult | null;
    onDismiss: () => void;
    vehicleTitle?: string;
}) {
    const [open, setOpen] = useState(true);
    const [visibleSteps, setVisibleSteps] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [dismissedValues, setDismissedValues] = useState<Set<string>>(new Set());

    // Phase 1 — animate steps 1 & 2 immediately when a new audit starts
    useEffect(() => {
        setVisibleSteps(0);
        setShowResult(false);
        setDismissedValues(new Set());
        setAddedKnownValues(new Set());
        setAddedNewValues(new Set());
        const t1 = setTimeout(() => setVisibleSteps(1), 200);
        const t2 = setTimeout(() => setVisibleSteps(2), 650);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [rowInfo]);

    // Phase 2 — complete step 3 and reveal result when API responds
    useEffect(() => {
        if (!result) return;
        setVisibleSteps(3);
        const t = setTimeout(() => setShowResult(true), 350);
        return () => clearTimeout(t);
    }, [result]);

    const isCorrect = result?.isCorrect;
    const confidence = result?.confidence ?? "medium";
    const recommendedValue = result?.recommendedValue ?? null;
    const reasoning = result?.reasoning ?? "";

    const confidenceColor: Record<string, string> = {
        high: "#16a34a", medium: "#ca8a04", low: "#dc2626",
    };

    // Pre-compute derived values used by both multi- and single-value paths
    const existingLower = new Set(rowInfo.expectedValues.map((v) => v.trim().toLowerCase()));
    const currentValueParts = new Set(
        rowInfo.currentValue !== "-" && rowInfo.currentValue !== ""
            ? rowInfo.currentValue.split("|").map((v) => v.trim().toLowerCase()).filter(Boolean)
            : [],
    );
    const isMultiValue = recommendedValue?.includes("|") ?? false;
    const allRecValues = isMultiValue
        ? recommendedValue!.split("|").map((v) => v.trim()).filter(Boolean)
        : [];
    // Values AI suggested that are NOT in known ratings → need "Add + Rate"
    const newMultiValues = allRecValues.filter((v) => !existingLower.has(v.trim().toLowerCase()));
    // Values AI suggested that ARE in known ratings but NOT yet in the current row value → quick "Add"
    const knownMultiValues = allRecValues.filter(
        (v) => existingLower.has(v.trim().toLowerCase()) && !currentValueParts.has(v.trim().toLowerCase()),
    );
    const [addedKnownValues, setAddedKnownValues] = useState<Set<string>>(new Set());
    const [addedNewValues, setAddedNewValues] = useState<Set<string>>(new Set());
    const visibleNewValues = newMultiValues.filter((v) => !dismissedValues.has(v));
    const visibleKnownValues = knownMultiValues;

    // For single-value: is the recommended value absent from the expected list?
    const isNewSingleValue =
        !isMultiValue &&
        rowInfo.isEnumerated &&
        recommendedValue !== null &&
        !existingLower.has(recommendedValue.trim().toLowerCase());

    return (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
            {/* Header */}
            <button
                onClick={() => setOpen((v) => !v)}
                style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", background: "none", border: "none",
                    borderBottom: open ? "1px solid #e2e8f0" : "none",
                    cursor: "pointer", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase" as const, letterSpacing: 0.8, color: "#6366f1",
                }}
            >
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="bi bi-robot" />
                    AI Audit
                    <span style={{ fontSize: 10, fontWeight: 500, textTransform: "none" as const, color: "#64748b", letterSpacing: 0 }}>
                        {vehicleTitle ? `${vehicleTitle} — ` : ""}{rowInfo.row.specName}
                    </span>
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 14, padding: 0, lineHeight: 1 }}
                        title="Dismiss"
                    >×</button>
                    <i className={`bi bi-chevron-${open ? "up" : "down"}`} style={{ fontSize: 12 }} />
                </span>
            </button>

            {open && (
                <div style={{ padding: "12px 14px" }}>
                    {/* Pipeline steps */}
                    <div style={{ marginBottom: 14 }}>
                        {PIPELINE_STEPS.map((step, i) => (
                            <div key={i} style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "4px 0",
                                opacity: visibleSteps > i ? 1 : 0.2,
                                transition: "opacity 0.3s ease",
                                fontSize: 12, color: visibleSteps > i ? "#374151" : "#9ca3af",
                            }}>
                                <span style={{
                                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 10, fontWeight: 700,
                                    background: visibleSteps > i ? "#6366f1" : "#e2e8f0",
                                    color: visibleSteps > i ? "#fff" : "#9ca3af",
                                    transition: "background 0.3s ease",
                                }}>{i + 1}</span>
                                {step}
                                {visibleSteps > i && <i className="bi bi-check2" style={{ color: "#16a34a", marginLeft: "auto" }} />}
                            </div>
                        ))}
                    </div>

                    {showResult && (
                        <>
                            {/* Current value */}
                            <div style={{ marginBottom: 12, padding: "8px 10px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, color: "#64748b", letterSpacing: 0.6, marginBottom: 3 }}>Current Value</div>
                                <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>
                                    {rowInfo.currentValue === "-" ? <em style={{ color: "#9ca3af" }}>not set</em> : `"${rowInfo.currentValue}"`}
                                </div>
                            </div>

                            {/* Verdict */}
                            <div style={{
                                display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
                                padding: "8px 10px", borderRadius: 8,
                                background: isCorrect ? "#f0fdf4" : "#fff5f5",
                                border: `1px solid ${isCorrect ? "#bbf7d0" : "#fecaca"}`,
                            }}>
                                <i className={`bi bi-${isCorrect ? "check-circle-fill" : "x-circle-fill"}`}
                                    style={{ fontSize: 16, color: isCorrect ? "#16a34a" : "#dc2626", flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: isCorrect ? "#15803d" : "#b91c1c" }}>
                                        {isCorrect ? "Value is correct" : "Value may be incorrect"}
                                    </div>
                                    {reasoning && (
                                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{reasoning}</div>
                                    )}
                                </div>
                                <span style={{
                                    fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                                    background: (confidenceColor[confidence] ?? "#9ca3af") + "18",
                                    color: confidenceColor[confidence] ?? "#9ca3af",
                                    border: `1px solid ${(confidenceColor[confidence] ?? "#9ca3af")}40`,
                                    textTransform: "uppercase" as const, letterSpacing: 0.5,
                                }}>
                                    {confidence}
                                </span>
                            </div>

                            {/* Recommended value + actions */}
                            {!isCorrect && recommendedValue && (
                                <div style={{ marginBottom: 4 }}>
                                    {isMultiValue ? (
                                        /* ── Multi-value path (pipe-separated) ── */
                                        <>
                                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, color: "#64748b", letterSpacing: 0.6, marginBottom: 8 }}>
                                                New Options to Add
                                                {newMultiValues.length > 0 && (
                                                    <span style={{ marginLeft: 6, fontWeight: 500, color: "#94a3b8" }}>
                                                        ({visibleNewValues.length} of {newMultiValues.length})
                                                    </span>
                                                )}
                                            </div>

                                            {newMultiValues.length === 0 ? (
                                                <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                                                    All recommended values are already in your expected list.
                                                </p>
                                            ) : visibleNewValues.length === 0 ? (
                                                <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                                                    All new options have been handled.
                                                </p>
                                            ) : (
                                                <div style={{ display: "flex", flexDirection: "column" as const, gap: 8, marginBottom: 10 }}>
                                                    {visibleNewValues.map((val) => {
                                                        const isAdded = addedNewValues.has(val);
                                                        return (
                                                            <div key={val} style={{
                                                                display: "flex", alignItems: "center", gap: 8,
                                                                padding: "7px 10px", borderRadius: 8,
                                                                background: isAdded ? "#f0fdf4" : "#f0f9ff",
                                                                border: `1px solid ${isAdded ? "#bbf7d0" : "#bae6fd"}`,
                                                                transition: "background 0.2s, border-color 0.2s",
                                                            }}>
                                                                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: isAdded ? "#15803d" : "#0369a1" }}>
                                                                    {val}
                                                                </span>
                                                                {isAdded ? (
                                                                    <span style={{
                                                                        padding: "3px 9px", borderRadius: 6,
                                                                        background: "#dcfce7", color: "#15803d",
                                                                        fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" as const,
                                                                    }}>
                                                                        <i className="bi bi-check-circle me-1" />Added
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => {
                                                                            result?.onSaveAsOption(val, true);
                                                                            setAddedNewValues((prev) => new Set(prev).add(val));
                                                                        }}
                                                                        title="Save as expected option + set rating, then add to row"
                                                                        style={{
                                                                            padding: "3px 9px", borderRadius: 6,
                                                                            border: "1px dashed #6366f1", background: "transparent",
                                                                            color: "#6366f1", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                                                            whiteSpace: "nowrap" as const,
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-plus-circle me-1" />Add + Rate
                                                                    </button>
                                                                )}
                                                                {!isAdded && (
                                                                    <button
                                                                        onClick={() => setDismissedValues((prev) => new Set(prev).add(val))}
                                                                        title="Dismiss this value"
                                                                        style={{
                                                                            width: 24, height: 24, borderRadius: "50%", padding: 0,
                                                                            border: "1px solid #e2e8f0", background: "#f8fafc",
                                                                            color: "#94a3b8", fontSize: 13, cursor: "pointer",
                                                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >×</button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Known values — already rated, not yet in row */}
                                            {visibleKnownValues.length > 0 && (
                                                <>
                                                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, color: "#64748b", letterSpacing: 0.6, marginBottom: 8, marginTop: newMultiValues.length > 0 ? 12 : 0 }}>
                                                        Already Rated — Add to Row
                                                    </div>
                                                    <div style={{ display: "flex", flexDirection: "column" as const, gap: 6, marginBottom: 10 }}>
                                                        {visibleKnownValues.map((val) => {
                                                            const isAdded = addedKnownValues.has(val.trim().toLowerCase());
                                                            return (
                                                                <div key={val} style={{
                                                                    display: "flex", alignItems: "center", gap: 8,
                                                                    padding: "6px 10px", borderRadius: 8,
                                                                    background: "#f0fdf4",
                                                                    border: `1px solid ${isAdded ? "#86efac" : "#bbf7d0"}`,
                                                                    opacity: isAdded ? 0.85 : 1,
                                                                    transition: "border-color 0.2s, opacity 0.2s",
                                                                }}>
                                                                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#15803d" }}>
                                                                        {val}
                                                                    </span>
                                                                    {isAdded ? (
                                                                        <span style={{
                                                                            padding: "3px 9px", borderRadius: 6,
                                                                            background: "#dcfce7", color: "#15803d",
                                                                            fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" as const,
                                                                        }}>
                                                                            <i className="bi bi-check-circle me-1" />Added
                                                                        </span>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => {
                                                                                result?.onAddKnownValue(val);
                                                                                setAddedKnownValues((prev) => new Set(prev).add(val.trim().toLowerCase()));
                                                                            }}
                                                                            style={{
                                                                                padding: "3px 9px", borderRadius: 6,
                                                                                border: "none", background: "#16a34a",
                                                                                color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                                                                whiteSpace: "nowrap" as const,
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-plus me-1" />Add
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </>
                                            )}

                                            <button
                                                onClick={onDismiss}
                                                style={{
                                                    width: "100%", padding: "7px 0", borderRadius: 8,
                                                    border: "1px solid #e2e8f0", background: "#f8fafc",
                                                    color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                                }}
                                            >
                                                Done
                                            </button>
                                        </>
                                    ) : (
                                        /* ── Single-value path ── */
                                        <>
                                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" as const, color: "#64748b", letterSpacing: 0.6, marginBottom: 6 }}>
                                                Recommended Value
                                            </div>
                                            <div style={{
                                                padding: "7px 10px", background: "#f0f9ff", border: "1px solid #bae6fd",
                                                borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#0369a1", marginBottom: 10,
                                            }}>
                                                "{recommendedValue}"
                                            </div>

                                            {isNewSingleValue && (
                                                <div style={{
                                                    display: "flex", alignItems: "flex-start", gap: 6,
                                                    padding: "7px 10px", marginBottom: 10, borderRadius: 8,
                                                    background: "#fffbeb", border: "1px solid #fde68a",
                                                    fontSize: 11, color: "#92400e",
                                                }}>
                                                    <i className="bi bi-exclamation-triangle-fill" style={{ flexShrink: 0, marginTop: 1 }} />
                                                    This value is not in the expected list. Set a rating first — it will be applied to the row automatically.
                                                </div>
                                            )}

                                            <div style={{ display: "flex", gap: 8, marginBottom: (rowInfo.isEnumerated && !isNewSingleValue) ? 10 : 0 }}>
                                                <button
                                                    onClick={() => {
                                                        if (isNewSingleValue) {
                                                            // Open modal; modal will apply value after rating is saved
                                                            result?.onSaveAsOption(recommendedValue, true);
                                                        } else {
                                                            result?.onAccept();
                                                            onDismiss();
                                                        }
                                                    }}
                                                    style={{
                                                        flex: 1, padding: "7px 0", borderRadius: 8,
                                                        border: "none", background: "#6366f1", color: "#fff",
                                                        fontSize: 12, fontWeight: 700, cursor: "pointer",
                                                    }}
                                                >
                                                    <i className={`bi bi-${isNewSingleValue ? "plus-circle" : "check2"} me-1`} />
                                                    {isNewSingleValue ? "Set Rating & Accept" : "Accept"}
                                                </button>
                                                <button
                                                    onClick={() => { result?.onReject(); onDismiss(); }}
                                                    style={{
                                                        flex: 1, padding: "7px 0", borderRadius: 8,
                                                        border: "1px solid #e2e8f0", background: "#f8fafc",
                                                        color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                                    }}
                                                >
                                                    <i className="bi bi-x me-1" />Reject
                                                </button>
                                            </div>

                                            {/* Show "Save as Expected Value" only for known values the user may want to rate */}
                                            {rowInfo.isEnumerated && !isNewSingleValue && (
                                                <button
                                                    onClick={() => result?.onSaveAsOption(recommendedValue)}
                                                    style={{
                                                        width: "100%", padding: "7px 0", borderRadius: 8,
                                                        border: "1px dashed #6366f1", background: "transparent",
                                                        color: "#6366f1", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                                    }}
                                                >
                                                    <i className="bi bi-plus-circle me-1" />Save as Expected Value + Set Rating
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {isCorrect && (
                                <button
                                    onClick={onDismiss}
                                    style={{
                                        width: "100%", padding: "7px 0", borderRadius: 8,
                                        border: "1px solid #e2e8f0", background: "#f8fafc",
                                        color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    }}
                                >
                                    Dismiss
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────

const MatchSpecificationVehiclePage = () => {
    const { vehicleId } = useParams<{ vehicleId: string }>();
    const navigate = useNavigate();

    const [matchedResult, setMatchedResult] = useState<any>(null);
    const [vehicleTitle, setVehicleTitle] = useState("");
    const [vehicleLoading, setVehicleLoading] = useState(true);
    const [matchLoading, setMatchLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [savingWithoutAudit, setSavingWithoutAudit] = useState(false);

    const [existingAuditBy, setExistingAuditBy] = useState<string | null>(null);
    const [existingAuditSign, setExistingAuditSign] = useState<string | null>(null);

    const [auditedCount, setAuditedCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [focusedRow, setFocusedRow] = useState<TableRow | null>(null);
    const [auditPendingRowInfo, setAuditPendingRowInfo] = useState<AuditResult["rowInfo"] | null>(null);
    const [currentAuditResult, setCurrentAuditResult] = useState<AuditResult | null>(null);

    const [expectedResults, setExpectedResults] = useState<any[]>([]);
    const [allRatings, setAllRatings] = useState<any[]>([]);
    const [computedScores, setComputedScores] = useState<{
        general: number; performance: number; safety: number;
        features: number; overall: number; customer: number;
    } | null>(null);

    useEffect(() => {
        Promise.all([
            API.getOneVehicle(vehicleId!),
            API.getExpectedResults(),
            API.getAllRatings(),
        ])
            .then(([vehicleRes, erRes, ratingsRes]: [any, any, any]) => {
                console.log("ratings:", ratingsRes.data);
                

                const v = vehicleRes.data;
                setMatchedResult(v.specification?.specification ?? null);
                setExistingAuditBy(v.specification?.audit_by ?? null);
                setExistingAuditSign(v.specification?.audit_sign ?? null);
                setVehicleTitle(v.title || `Vehicle ${vehicleId}`);
                setExpectedResults(erRes.data || []);
                setAllRatings(
                    Array.isArray(ratingsRes.data) ? ratingsRes.data : [],
                );
                setVehicleLoading(false);
            })
            .catch(() => setVehicleLoading(false));
    }, [vehicleId]);

    const handleSaveWithoutAudit = async () => {
        if (!matchedResult) return;
        setSavingWithoutAudit(true);
        setSaveSuccess(false);
        try {
            await API.saveSpecification(vehicleId!, matchedResult, computedScores ?? undefined);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error("Failed to save specification:", err);
        } finally {
            setSavingWithoutAudit(false);
        }
    };

    const handleSave = async (auditSign: string) => {
        if (!matchedResult) return;
        setSaving(true);
        setSaveSuccess(false);
        try {
            const auditBy = localStorage.getItem("user_email") ?? "";
            await API.saveSpecification(
                vehicleId!,
                matchedResult,
                computedScores ?? undefined,
                undefined,
                auditSign,
                auditBy,
            );
            setSaveSuccess(true);
            setExistingAuditBy(localStorage.getItem("user_email") ?? "");
            setExistingAuditSign(auditSign);
            setTimeout(() => navigate(-1), 1800);
        } catch (err) {
            console.error("Failed to save specification:", err);
        } finally {
            setSaving(false);
        }
    };

    if (vehicleLoading) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{ width: 36, height: 36 }}
                />
            </div>
        );
    }

    return (
        <div style={{ marginTop: 80, paddingBottom: 60 }}>
            {/* Page header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "16px 24px",
                    borderBottom: "1px solid #e2e8f0",
                    marginBottom: 20,
                }}
            >
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <i className="bi bi-chevron-left" />
                </button>
                <div>
                    <h5
                        style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}
                    >
                        Match Specification
                    </h5>
                    {vehicleTitle && (
                        <div
                            style={{
                                fontSize: 13,
                                color: "#64748b",
                                marginTop: 2,
                            }}
                        >
                            {vehicleTitle}
                        </div>
                    )}
                </div>
                {saveSuccess && (
                    <span
                        style={{
                            marginLeft: "auto",
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#16a34a",
                            background: "#f0fdf4",
                            border: "1px solid #bbf7d0",
                            padding: "4px 12px",
                            borderRadius: 20,
                        }}
                    >
                        ✓ Saved successfully
                    </span>
                )}
            </div>

            {/* Two-column layout */}
            <div
                style={{
                    display: "flex",
                    gap: 20,
                    padding: "0 24px",
                    alignItems: "flex-start",
                }}
            >
                {/* Left column */}
                <div style={{ flex: "0 0 62%", minWidth: 0 }}>
                    <Input
                        handleUpdateResult={(newResult: any) => {
                            // Input fires on mount with its initial empty `{}`.
                            // Block that so it never overwrites a spec already loaded from the server.
                            if (!newResult || Object.keys(newResult).length === 0) return;
                            setMatchedResult(newResult);
                        }}
                        loading={matchLoading}
                        setLoading={setMatchLoading}
                    />
                    <MatchSpecificationOutput
                        result={matchedResult}
                        vehicleId={vehicleId!}
                        loading={matchLoading}
                        vehicleTitle={vehicleTitle}
                        isAudited={!!existingAuditBy}
                        onResultChange={setMatchedResult}
                        onRowFocus={(row) => setFocusedRow(row)}
                        onAuditChange={(audited, total) => {
                            setAuditedCount(audited);
                            setTotalCount(total);
                        }}
                        allRatings={allRatings}
                        expectedResults={expectedResults}
                        onRatingCreated={(newRating) => setAllRatings(prev => [...prev, newRating])}
                        onExpectedResultUpdated={(updatedEr) =>
                            setExpectedResults((prev) =>
                                prev.map((er) => er._id === updatedEr._id ? updatedEr : er)
                            )
                        }
                        onAuditStart={(ri) => { setAuditPendingRowInfo(ri); setCurrentAuditResult(null); setFocusedRow(ri.row); }}
                        onAuditResult={(res) => setCurrentAuditResult(res)}
                        onScoresComputed={setComputedScores}
                    />
                </div>

                {/* Right sticky sidebar */}
                <div
                    style={{
                        flex: "0 0 36%",
                        position: "sticky",
                        top: 90,
                        alignSelf: "flex-start",
                        maxHeight: "calc(100vh - 100px)",
                        overflowY: "auto",
                    }}
                >
                    {auditPendingRowInfo && (
                        <AuditResultCard
                            rowInfo={auditPendingRowInfo}
                            result={currentAuditResult}
                            onDismiss={() => { setAuditPendingRowInfo(null); setCurrentAuditResult(null); }}
                            vehicleTitle={vehicleTitle}
                        />
                    )}
                    <RowDetailSection
                        row={focusedRow}
                        expectedResults={expectedResults}
                        allRatings={allRatings}
                        onRatingDeleted={(id) => setAllRatings((prev) => prev.filter((r) => r._id !== id))}
                    />
                    <AuditPanel
                        auditedCount={auditedCount}
                        totalCount={totalCount}
                        onSave={handleSave}
                        saving={saving}
                        existingAuditBy={existingAuditBy}
                        existingAuditSign={existingAuditSign}
                        onAuditAgain={() => {
                            setExistingAuditBy(null);
                            setExistingAuditSign(null);
                        }}
                        onSaveWithoutAudit={handleSaveWithoutAudit}
                        savingWithoutAudit={savingWithoutAudit}
                        hasResult={!!matchedResult}
                    />
                </div>
            </div>
        </div>
    );
};

export default MatchSpecificationVehiclePage;
